import { useState } from 'react';
import { Button } from '@launchpad-ui/components';

import { Card, ErrorMessage, Field, PageHeader } from '../components/ui';
import { useCreateReviewer, useDeleteReviewer, useReviewers, useUpdateReviewer } from '../lib/hooks/useReviewers';
import type { Reviewer } from '../lib/types';

import styles from '../styles/common.module.css';

type ReviewerForm = { name: string; email: string };
const EMPTY: ReviewerForm = { name: '', email: '' };

export function Reviewers() {
  const reviewersQuery = useReviewers();
  const createMutation = useCreateReviewer();
  const updateMutation = useUpdateReviewer();
  const deleteMutation = useDeleteReviewer();

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<ReviewerForm>(EMPTY);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<ReviewerForm>(EMPTY);
  const [error, setError] = useState<string | null>(null);

  const reviewers = reviewersQuery.data ?? [];
  const active = reviewers.filter((r) => r.active);
  const inactive = reviewers.filter((r) => !r.active);

  const submitNew = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await createMutation.mutateAsync(form);
      setForm(EMPTY);
      setShowForm(false);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const startEdit = (r: Reviewer) => {
    setEditingId(r.id);
    setEditForm({ name: r.name, email: r.email ?? '' });
    setError(null);
  };

  const saveEdit = async (id: number) => {
    setError(null);
    try {
      await updateMutation.mutateAsync({ id, patch: editForm });
      setEditingId(null);
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const toggle = (r: Reviewer) => updateMutation.mutate({ id: r.id, patch: { active: r.active ? 0 : 1 } });

  const remove = (id: number) => {
    if (window.confirm('Delete this reviewer? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  return (
    <div className={styles.pageNarrow}>
      <PageHeader
        title="Reviewers"
        subtitle={`${active.length} active`}
        actions={
          <Button
            onPress={() => {
              setShowForm((v) => !v);
              setError(null);
            }}
          >
            {showForm ? 'Cancel' : '+ Add Reviewer'}
          </Button>
        }
      />

      {showForm && (
        <form onSubmit={submitNew}>
          <Card>
            <h2 className={styles.sectionTitle}>New Reviewer</h2>
            <ErrorMessage message={error} />
            <div className={styles.formGrid}>
              <Field label="Name" required>
                <input
                  className={styles.input}
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Jordan"
                />
              </Field>
              <Field label="Email">
                <input
                  className={styles.input}
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                  placeholder="jordan@launchdarkly.com"
                />
              </Field>
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="submit" isDisabled={createMutation.isPending}>
                {createMutation.isPending ? 'Saving…' : 'Add Reviewer'}
              </Button>
            </div>
          </Card>
        </form>
      )}

      {!showForm && <ErrorMessage message={error} />}

      <ReviewerSection
        title="Active"
        reviewers={active}
        editingId={editingId}
        editForm={editForm}
        setEditForm={setEditForm}
        onEdit={startEdit}
        onSaveEdit={saveEdit}
        onCancelEdit={() => setEditingId(null)}
        onToggle={toggle}
        onDelete={remove}
        saving={updateMutation.isPending}
      />

      {inactive.length > 0 && (
        <ReviewerSection
          title="Inactive"
          dimmed
          reviewers={inactive}
          editingId={editingId}
          editForm={editForm}
          setEditForm={setEditForm}
          onEdit={startEdit}
          onSaveEdit={saveEdit}
          onCancelEdit={() => setEditingId(null)}
          onToggle={toggle}
          onDelete={remove}
          saving={updateMutation.isPending}
        />
      )}

      {reviewersQuery.isLoading && <div className={styles.empty}>Loading…</div>}
      {!reviewersQuery.isLoading && reviewers.length === 0 && <div className={styles.empty}>No reviewers yet.</div>}
    </div>
  );
}

function ReviewerSection({
  title,
  reviewers,
  editingId,
  editForm,
  setEditForm,
  onEdit,
  onSaveEdit,
  onCancelEdit,
  onToggle,
  onDelete,
  saving,
  dimmed,
}: {
  title: string;
  reviewers: Reviewer[];
  editingId: number | null;
  editForm: ReviewerForm;
  setEditForm: (f: ReviewerForm) => void;
  onEdit: (r: Reviewer) => void;
  onSaveEdit: (id: number) => void;
  onCancelEdit: () => void;
  onToggle: (r: Reviewer) => void;
  onDelete: (id: number) => void;
  saving: boolean;
  dimmed?: boolean;
}) {
  if (!reviewers.length) return null;
  return (
    <div className={dimmed ? styles.dimmed : ''} style={{ marginBottom: '2rem' }}>
      <h2 className={styles.sectionTitle}>
        {title} ({reviewers.length})
      </h2>
      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th className={styles.th}>Name</th>
              <th className={styles.th}>Email</th>
              <th className={styles.th} />
            </tr>
          </thead>
          <tbody>
            {reviewers.map((r) =>
              editingId === r.id ? (
                <tr key={r.id}>
                  <td className={styles.td}>
                    <input
                      className={styles.input}
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                    />
                  </td>
                  <td className={styles.td}>
                    <input
                      className={styles.input}
                      type="email"
                      value={editForm.email}
                      onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                    />
                  </td>
                  <td className={styles.td}>
                    <div className={styles.actionsCell}>
                      <Button size="small" onPress={() => onSaveEdit(r.id)} isDisabled={saving}>
                        Save
                      </Button>
                      <Button size="small" variant="minimal" onPress={onCancelEdit}>
                        Cancel
                      </Button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={r.id}>
                  <td className={styles.td} style={{ fontWeight: 600 }}>
                    {r.name}
                  </td>
                  <td className={styles.td}>{r.email || '—'}</td>
                  <td className={styles.td}>
                    <div className={styles.actionsCell}>
                      <Button size="small" variant="minimal" onPress={() => onEdit(r)}>
                        Edit
                      </Button>
                      <Button size="small" variant="minimal" onPress={() => onToggle(r)}>
                        {r.active ? 'Deactivate' : 'Reactivate'}
                      </Button>
                      <Button size="small" variant="minimal" onPress={() => onDelete(r.id)}>
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ),
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
